import { usePlaybackVideoJsPlayer } from '@app/core/playback/video/useVideoPlaybackState';
import { useEffect, useMemo } from 'react';
import Player from 'video.js/dist/types/player';
import { DescriptionChapter, parseDescriptionChapters } from './parse-description-chapters';

type FragmentChaptersAttachmentProps = {
  description: string | null | undefined;
};

// ponytail: open-ended last cue; video.js clamps display to the real duration.
const LAST_CUE_END = 24 * 3600;

function toVtt(chapters: DescriptionChapter[]): string {
  const ts = (s: number) => new Date(s * 1000).toISOString().substring(11, 23);
  const cues = chapters.map((c, i) => {
    const end = chapters[i + 1]?.start ?? LAST_CUE_END;
    return `${ts(c.start)} --> ${ts(Math.max(end, c.start + 1))}\n${c.title}`;
  });
  return `WEBVTT\n\n${cues.join('\n\n')}\n`;
}

function renderMarkers(player: Player, chapters: DescriptionChapter[]) {
  const holder = player.el().querySelector('.vjs-progress-holder');
  const duration = player.duration();
  if (!holder || !duration || !Number.isFinite(duration)) {
    return;
  }
  holder.querySelectorAll('.vjs-chapter-marker').forEach((el) => el.remove());
  chapters.forEach((c) => {
    const marker = document.createElement('div');
    marker.className = 'vjs-chapter-marker';
    marker.style.left = `${Math.min((c.start / duration) * 100, 100)}%`;
    marker.title = c.title;
    holder.appendChild(marker);
  });
}

/**
 * Invisible helper mounted under VideoPlaybackProvider: parses YouTube-style chapters from the
 * fragment description and registers them on the Video.js player as a native `chapters` text
 * track (control-bar chapters menu) plus markers on the progress bar.
 */
export function FragmentChaptersAttachment({ description }: FragmentChaptersAttachmentProps) {
  const player = usePlaybackVideoJsPlayer();
  const chapters = useMemo(() => parseDescriptionChapters(description), [description]);

  useEffect(() => {
    if (!player || player.isDisposed() || chapters.length === 0) {
      return;
    }

    let cancelled = false;
    let trackElement: ReturnType<Player['addRemoteTextTrack']> | undefined;
    const src = URL.createObjectURL(new Blob([toVtt(chapters)], { type: 'text/vtt' }));
    const onDuration = () => renderMarkers(player, chapters);

    const attach = () => {
      if (cancelled || player.isDisposed()) {
        return;
      }
      trackElement = player.addRemoteTextTrack(
        { kind: 'chapters', src, label: 'Capítulos', default: true },
        true,
      );
      onDuration();
      player.on('durationchange', onDuration);
    };

    player.ready(() => {
      if (cancelled || player.isDisposed()) {
        return;
      }
      if (player.readyState() >= 1) {
        attach();
      } else {
        player.one('loadedmetadata', attach);
      }
    });

    return () => {
      cancelled = true;
      URL.revokeObjectURL(src);
      if (player.isDisposed()) {
        return;
      }
      player.off('loadedmetadata', attach);
      player.off('durationchange', onDuration);
      player
        .el()
        ?.querySelectorAll('.vjs-chapter-marker')
        .forEach((el) => el.remove());
      if (trackElement) {
        player.removeRemoteTextTrack(trackElement);
      }
    };
  }, [player, chapters]);

  return null;
}
